/** Table shapes and seat placement, shared by the map renderer and wayfinding. */

export interface TableGeom {
  shape: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  max_covers: number;
}

export interface Seat {
  /** Position relative to the table centre, before rotation (metres). */
  x: number;
  y: number;
  /** Direction the chair faces (radians), towards the table. */
  angle: number;
}

export const CHAIR_SIZE = 0.42;
const CHAIR_GAP = 0.08;

export function isRoundShape(shape: string): boolean {
  return shape === 'round' || shape === 'oval' || shape === 'high';
}

/** Where to draw chairs around a table, in the table's own frame. Booths have benches instead. */
export function seatPositions(t: Pick<TableGeom, 'shape' | 'width' | 'height' | 'max_covers'>): Seat[] {
  const seats = Math.max(0, Math.min(t.max_covers, 24));
  if (t.shape === 'booth' || seats === 0) return [];
  const out: Seat[] = [];
  if (isRoundShape(t.shape)) {
    const rx = t.width / 2 + CHAIR_SIZE / 2 + CHAIR_GAP;
    const ry = t.height / 2 + CHAIR_SIZE / 2 + CHAIR_GAP;
    for (let i = 0; i < seats; i++) {
      const a = (i / seats) * Math.PI * 2 - Math.PI / 2;
      const x = Math.cos(a) * rx;
      const y = Math.sin(a) * ry;
      out.push({ x, y, angle: Math.atan2(-y, -x) });
    }
    return out;
  }
  // Rectangles: fill the long sides first, then the ends.
  const horizontal = t.width >= t.height;
  const long = horizontal ? t.width : t.height;
  const short = horizontal ? t.height : t.width;
  const perLong = Math.max(1, Math.floor((long + 0.1) / 0.6));
  let alongLong = Math.min(perLong, Math.ceil(seats / 2));
  let ends = 0;
  if (seats > alongLong * 2) {
    alongLong = perLong;
    ends = Math.min(2, seats - perLong * 2);
  }
  const sides: Array<[number, number]> = [];
  let remaining = seats - ends;
  // Split remaining between the two long sides (first side gets the extra one).
  const first = Math.min(alongLong, Math.ceil(remaining / 2));
  const second = Math.min(alongLong, remaining - first);
  sides.push([-1, first], [1, second]);
  const offset = short / 2 + CHAIR_SIZE / 2 + CHAIR_GAP;
  for (const [side, count] of sides) {
    for (let i = 0; i < count; i++) {
      const along = ((i + 0.5) / count - 0.5) * long;
      const x = horizontal ? along : side * offset;
      const y = horizontal ? side * offset : along;
      out.push({ x, y, angle: Math.atan2(-y, -x) });
    }
  }
  remaining = ends;
  const endOffset = long / 2 + CHAIR_SIZE / 2 + CHAIR_GAP;
  for (let i = 0; i < remaining; i++) {
    const side = i === 0 ? -1 : 1;
    const x = horizontal ? side * endOffset : 0;
    const y = horizontal ? 0 : side * endOffset;
    out.push({ x, y, angle: Math.atan2(-y, -x) });
  }
  return out;
}

/** Outline of a table (plus padding) in plan coordinates, as a polygon. */
export function tableFootprint(t: TableGeom, pad = 0): Array<[number, number]> {
  const a = (t.rotation * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const local: Array<[number, number]> = [];
  // Include chairs/benches in the footprint, roughly.
  const extra = t.shape === 'booth' ? 0.55 : CHAIR_SIZE * 0.6;
  if (isRoundShape(t.shape)) {
    const rx = t.width / 2 + pad + extra;
    const ry = t.height / 2 + pad + extra;
    for (let i = 0; i < 16; i++) {
      const ang = (i / 16) * Math.PI * 2;
      local.push([Math.cos(ang) * rx, Math.sin(ang) * ry]);
    }
  } else {
    const hw = t.width / 2 + pad + (t.shape === 'booth' ? 0.05 : extra);
    const hh = t.height / 2 + pad + extra;
    local.push([-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]);
  }
  return local.map(([x, y]) => [t.x + x * cos - y * sin, t.y + x * sin + y * cos]);
}

export function pointInPolygon(x: number, y: number, poly: Array<[number, number]>): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i]!;
    const [xj, yj] = poly[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
